import React, { useEffect, useMemo, useState } from "react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SETTING = {
  sale_mode: "retail_only",
  pricing_method: "percentage",
  discount_percent: 0,
  wholesale_price: 0,
  moq: 1,
};

function getProductImage(product) {
  return product?.image?.src || product?.images?.[0]?.src || "";
}

function getRetailPriceValue(product) {
  const prices = (product?.variants || [])
    .map((variant) => Number(variant?.price))
    .filter((price) => Number.isFinite(price));

  if (prices.length === 0) {
    return 0;
  }

  return Math.min(...prices);
}

function getRetailPriceLabel(product) {
  const prices = (product?.variants || [])
    .map((variant) => Number(variant?.price))
    .filter((price) => Number.isFinite(price));

  if (prices.length === 0) {
    return "-";
  }

  const lowestPrice = Math.min(...prices);
  const highestPrice = Math.max(...prices);

  if (lowestPrice === highestPrice) {
    return `$${lowestPrice.toFixed(2)}`;
  }

  return `$${lowestPrice.toFixed(2)} – $${highestPrice.toFixed(2)}`;
}

export default function WholesaleProducts() {
  const [products, setProducts] = useState([]);
  const [wholesaleSettings, setWholesaleSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    loadProductsAndSettings();
  }, []);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return products;
    }

    return products.filter((product) =>
      String(product?.title || "")
        .toLowerCase()
        .includes(query)
    );
  }, [products, search]);

  function updateSetting(productId, field, value) {
    const key = String(productId);

    setWholesaleSettings((current) => ({
      ...current,
      [key]: {
        ...DEFAULT_SETTING,
        ...current[key],
        [field]: value,
      },
    }));
  }

  async function loadProductsAndSettings() {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const [productsResponse, settingsResponse] = await Promise.all([
        fetch(`${API_BASE}/shopify/products`),
        fetch(`${API_BASE}/wholesale-products/settings`),
      ]);

      if (!productsResponse.ok) {
        throw new Error(
          `Unable to load Shopify products. HTTP ${productsResponse.status}`
        );
      }

      if (!settingsResponse.ok) {
        throw new Error(
          `Unable to load wholesale settings. HTTP ${settingsResponse.status}`
        );
      }

      const productsData = await productsResponse.json();
      const savedData = await settingsResponse.json();

      const importedProducts = Array.isArray(productsData)
        ? productsData
        : [];

      const savedProducts = Array.isArray(savedData?.products)
        ? savedData.products
        : [];

      const savedSettingsMap = {};

      savedProducts.forEach((item) => {
        const productId = String(item.shopify_product_id || "");

        if (productId) {
          savedSettingsMap[productId] = item;
        }
      });

      const mergedSettings = {};

      importedProducts.forEach((product) => {
        const productId = String(product.id);

        mergedSettings[productId] = {
          ...DEFAULT_SETTING,
          ...(savedSettingsMap[productId] || {}),
        };
      });

      setProducts(importedProducts);
      setWholesaleSettings(mergedSettings);

      setMessage(
        `Loaded ${importedProducts.length} Shopify products. Previously saved wholesale settings were restored.`
      );
    } catch (err) {
      console.error(err);

      setError(
        err instanceof Error
          ? err.message
          : "Unable to load Shopify products."
      );
    } finally {
      setLoading(false);
    }
  }

  async function saveWholesaleSettings() {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payloadProducts = products.map((product) => {
        const productId = String(product.id);

        const setting = {
          ...DEFAULT_SETTING,
          ...(wholesaleSettings[productId] || {}),
        };

        return {
          shopify_product_id: productId,
          title: product.title || "",
          image: getProductImage(product),
          retail_price: getRetailPriceValue(product),
          sale_mode: setting.sale_mode,
          pricing_method: setting.pricing_method,
          discount_percent: Number(setting.discount_percent) || 0,
          wholesale_price: Number(setting.wholesale_price) || 0,
          moq: Math.max(1, Number(setting.moq) || 1),
        };
      });

      const response = await fetch(
        `${API_BASE}/wholesale-products/settings`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            products: payloadProducts,
          }),
        }
      );

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(
          responseText ||
            `Unable to save wholesale settings. HTTP ${response.status}`
        );
      }

      const result = JSON.parse(responseText);

      setMessage(
        result.message ||
          `Saved ${payloadProducts.length} wholesale product settings.`
      );
    } catch (err) {
      console.error(err);

      setError(
        err instanceof Error
          ? err.message
          : "Unable to save wholesale settings."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: "24px" }}>
      <div style={topBarStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Wholesale Products</h1>

          <p style={{ marginTop: "8px", color: "#666" }}>
            Select which Shopify products are retail, wholesale, or both.
          </p>
        </div>

        <div style={buttonGroupStyle}>
          <button
            type="button"
            onClick={loadProductsAndSettings}
            disabled={loading || saving}
            style={secondaryButtonStyle}
          >
            {loading ? "Syncing..." : "Sync Shopify Products"}
          </button>

          <button
            type="button"
            onClick={saveWholesaleSettings}
            disabled={loading || saving || products.length === 0}
            style={primaryButtonStyle}
          >
            {saving ? "Saving..." : "Save Wholesale Settings"}
          </button>
        </div>
      </div>

      {message && <div style={successStyle}>{message}</div>}

      {error && <div style={errorStyle}>{error}</div>}

      <div style={toolbarStyle}>
        <input
          type="search"
          placeholder="Search products..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={searchInputStyle}
        />

        <div style={{ color: "#555" }}>
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: "#f5f6f8" }}>
              <th style={headerStyle}>Product</th>
              <th style={headerStyle}>Retail Price</th>
              <th style={headerStyle}>Sale Type</th>
              <th style={headerStyle}>Pricing Method</th>
              <th style={headerStyle}>Discount %</th>
              <th style={headerStyle}>Fixed Wholesale Price</th>
              <th style={headerStyle}>MOQ</th>
            </tr>
          </thead>

          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="7" style={emptyStyle}>
                  {loading
                    ? "Loading Shopify products..."
                    : "No products found."}
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => {
                const productId = String(product.id);

                const setting = {
                  ...DEFAULT_SETTING,
                  ...(wholesaleSettings[productId] || {}),
                };

                const wholesaleEnabled =
                  setting.sale_mode !== "retail_only";

                return (
                  <tr key={productId}>
                    <td style={cellStyle}>
                      <div style={productStyle}>
                        {getProductImage(product) ? (
                          <img
                            src={getProductImage(product)}
                            alt={product.title || "Shopify product"}
                            style={imageStyle}
                          />
                        ) : (
                          <div style={noImageStyle}>No image</div>
                        )}

                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {product.title || "Untitled product"}
                          </div>

                          <div style={productIdStyle}>
                            Shopify ID: {productId}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={cellStyle}>
                      {getRetailPriceLabel(product)}
                    </td>

                    <td style={cellStyle}>
                      <select
                        value={setting.sale_mode}
                        onChange={(event) =>
                          updateSetting(
                            productId,
                            "sale_mode",
                            event.target.value
                          )
                        }
                        style={inputStyle}
                      >
                        <option value="retail_only">Retail only</option>
                        <option value="wholesale_retail">
                          Wholesale + Retail
                        </option>
                        <option value="wholesale_only">
                          Wholesale only
                        </option>
                      </select>
                    </td>

                    <td style={cellStyle}>
                      <select
                        value={setting.pricing_method}
                        disabled={!wholesaleEnabled}
                        onChange={(event) =>
                          updateSetting(
                            productId,
                            "pricing_method",
                            event.target.value
                          )
                        }
                        style={{
                          ...inputStyle,
                          opacity: wholesaleEnabled ? 1 : 0.5,
                        }}
                      >
                        <option value="percentage">
                          Percentage discount
                        </option>
                        <option value="fixed">
                          Fixed wholesale price
                        </option>
                      </select>
                    </td>

                    <td style={cellStyle}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={setting.discount_percent}
                        disabled={
                          !wholesaleEnabled ||
                          setting.pricing_method !== "percentage"
                        }
                        onChange={(event) =>
                          updateSetting(
                            productId,
                            "discount_percent",
                            Number(event.target.value)
                          )
                        }
                        style={inputStyle}
                      />
                    </td>

                    <td style={cellStyle}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={setting.wholesale_price}
                        disabled={
                          !wholesaleEnabled ||
                          setting.pricing_method !== "fixed"
                        }
                        onChange={(event) =>
                          updateSetting(
                            productId,
                            "wholesale_price",
                            Number(event.target.value)
                          )
                        }
                        style={inputStyle}
                      />
                    </td>

                    <td style={cellStyle}>
                      <input
                        type="number"
                        min="1"
                        value={setting.moq}
                        disabled={!wholesaleEnabled}
                        onChange={(event) =>
                          updateSetting(
                            productId,
                            "moq",
                            Math.max(1, Number(event.target.value))
                          )
                        }
                        style={inputStyle}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const topBarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const buttonGroupStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const primaryButtonStyle = {
  padding: "11px 18px",
  border: "none",
  borderRadius: "6px",
  background: "#1a2340",
  color: "#ffffff",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "11px 18px",
  border: "1px solid #1a2340",
  borderRadius: "6px",
  background: "#ffffff",
  color: "#1a2340",
  fontWeight: 600,
  cursor: "pointer",
};

const successStyle = {
  padding: "12px",
  marginBottom: "16px",
  borderRadius: "6px",
  background: "#ecfdf5",
  color: "#166534",
};

const errorStyle = {
  padding: "12px",
  marginBottom: "16px",
  border: "1px solid #dc2626",
  borderRadius: "6px",
  background: "#fef2f2",
  color: "#991b1b",
};

const toolbarStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "16px",
};

const searchInputStyle = {
  width: "320px",
  maxWidth: "100%",
  padding: "10px",
  border: "1px solid #ccc",
  borderRadius: "6px",
};

const tableStyle = {
  width: "100%",
  minWidth: "1150px",
  borderCollapse: "collapse",
  background: "#ffffff",
};

const headerStyle = {
  padding: "12px",
  border: "1px solid #ddd",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const cellStyle = {
  padding: "12px",
  border: "1px solid #ddd",
  verticalAlign: "middle",
};

const emptyStyle = {
  padding: "30px",
  textAlign: "center",
  border: "1px solid #ddd",
  color: "#666",
};

const productStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: "300px",
};

const imageStyle = {
  width: "64px",
  height: "64px",
  objectFit: "cover",
  borderRadius: "6px",
  border: "1px solid #ddd",
};

const noImageStyle = {
  width: "64px",
  height: "64px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "6px",
  background: "#f3f4f6",
  color: "#777",
  fontSize: "12px",
};

const productIdStyle = {
  marginTop: "4px",
  color: "#777",
  fontSize: "12px",
};

const inputStyle = {
  width: "100%",
  minWidth: "120px",
  boxSizing: "border-box",
  padding: "8px",
  border: "1px solid #ccc",
  borderRadius: "5px",
};
